import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Form';
import { TimeSelect } from '@/components/TimeSelect';
import { Icon } from '@/components/Icon';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useOptionalToast } from '@/components/Toast';
import { scheduleDatesApi } from '@/api/scheduleDates';
import { extractApiError } from '@/api/client';
import type { CalendarEventDto, CalendarRenewalDto, CalendarReplacementDto } from '@bhops/shared';
import { CATEGORY_META, type CalendarCategory } from './calendar-categories';

// 통합 캘린더 우클릭 — 2026-10-20 오너 "캘린더에 등록된 일정을 우클릭하면 수정, 삭제 중 고를 수 있는
// 버튼이 나와야해. 삭제는 당연히 한번 더 물어보는 경고창이 나온 다음 확정하면 삭제".
//   · 수정: 날짜 · 시각(선택, 비우면 하루 종일) · (캘린더에서 잡은 유지보수면) 메모
//   · 삭제: 확인창 → 인프라 날짜 · 회수일은 그 날짜만 비우고, 캘린더에서 잡은 유지보수는 서비스
//     오더와 고장 조치 이력을 함께 지운다(자동 예방정비는 날짜만 비운다). 구글 캘린더도 1분 안에.

export type ScheduleMove =
  | { kind: 'infra'; id: string; field: 'order' | 'delivery' | 'install' }
  | { kind: 'maintenance'; id: string }
  | { kind: 'return'; id: string }
  /** 2026-10-20 — 로봇 교체 일정 id(묶음 번호 — 한 대면 '로봇 교체' 서비스 오더 id). */
  | { kind: 'replace'; id: string }
  /** 2026-10-22 — 재계약 일정 id. */
  | { kind: 'renewal'; id: string }
  /** 2026-11-02 — 이벤트 일정 id 와 그 칩이 설치일인지 회수일인지. */
  | { kind: 'event'; id: string; side: 'install' | 'return' };

/** 우클릭한 일정 — 캘린더 칩의 모양 그대로. */
export interface ScheduleTarget {
  id: string;
  category: CalendarCategory;
  title: string;
  date: string;
  time?: string | null;
  /** 캘린더에서 잡은 유지보수의 메모. */
  memo?: string | null;
  /** 캘린더에서 잡은 유지보수(고객사 리포트 — 옛 일정은 고장 조치 이력 — 가 이어진 오더)인가 — 메모 수정 · 함께 삭제. */
  fromCalendar?: boolean;
  /** 2026-11-02 — 그 유지보수가 올린 고객사 리포트의 번호. 없으면 옛 일정(고장 조치 이력). */
  reportNo?: string | null;
  move?: ScheduleMove;
  /** 로봇 교체 일정 — 마쳤는지(수정 · 삭제 안내가 달라진다). */
  replacement?: CalendarReplacementDto;
  /** 재계약 일정 — 마쳤는지(수정 · 삭제 안내가 달라진다). */
  renewal?: CalendarRenewalDto;
  /** 이벤트 일정 — 삭제 안내(배정한 로봇이 풀린다). */
  calendarEvent?: CalendarEventDto;
}

/** 마친 교체가 하나라도 있는 로봇 교체 일정인가. */
const hasDoneSwap = (t: ScheduleTarget) => !!t.replacement?.items.some((i) => i.done);

/**
 * 캘린더에서 잡은 유지보수의 메모 — 오더 설명 `유지보수 — 고객사\n메모` 의 둘째 줄부터
 * (서버 calendar-plan 의 maintenanceMemo 와 같은 규칙). 그 꼴이 아니면 ''.
 */
export function maintenanceMemoOf(description: string | null | undefined): string {
  const d = (description ?? '').replace(/\r\n/g, '\n');
  if (!d.startsWith('유지보수 — ')) return '';
  const i = d.indexOf('\n');
  return i < 0 ? '' : d.slice(i + 1).trim();
}

async function refreshAfterChange(qc: QueryClient) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['dashboard'] }),
    qc.invalidateQueries({ queryKey: ['infrastructure-orders'] }),
    qc.invalidateQueries({ queryKey: ['fault-remediations'] }),
    qc.invalidateQueries({ queryKey: ['service-orders'] }),
    // 로봇 교체를 되돌리거나 실제 교체일을 고치면 배치 · 로봇이 바뀐다.
    qc.invalidateQueries({ queryKey: ['deployments'] }),
    qc.invalidateQueries({ queryKey: ['robots'] }),
    qc.invalidateQueries({ queryKey: ['new-schedule'] }),
    qc.invalidateQueries({ queryKey: ['assignments'] }),
  ]);
}

// ── 우클릭 메뉴 ──────────────────────────────────────────────────────────

export function ScheduleContextMenu({
  x,
  y,
  onEdit,
  onDelete,
  onComplete,
  completeLabel = '교체 완료',
  onUndo,
  onClose,
}: {
  x: number;
  y: number;
  onEdit: () => void;
  onDelete: () => void;
  /** 2026-10-20 — 로봇 교체 일정이면 [교체 완료]. 2026-10-22 — 재계약이면 [재계약 완료]. */
  onComplete?: () => void;
  completeLabel?: string;
  /** 2026-10-22 — 마친 재계약이면 [되돌리기]. */
  onUndo?: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const away = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('contextmenu', away, true);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    ref.current?.querySelector('button')?.focus();
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('contextmenu', away, true);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  // 화면 끝에서 눌러도 메뉴가 잘리지 않게.
  const left = Math.min(x, window.innerWidth - 150);
  const top = Math.min(y, window.innerHeight - (onComplete || onUndo ? 132 : 96));
  return (
    <div ref={ref} role="menu" style={{ ...menu, left, top }} data-testid="cal-menu">
      {onComplete && (
        <button
          type="button"
          role="menuitem"
          className="kuun-menu-item"
          style={{ ...menuItem, color: 'var(--cal-replace)' }}
          onClick={onComplete}
          data-testid="cal-menu-complete"
        >
          <Icon name="check-circle" size={14} aria-hidden />
          {completeLabel}
        </button>
      )}
      {onUndo && (
        <button
          type="button"
          role="menuitem"
          className="kuun-menu-item"
          style={{ ...menuItem, color: 'var(--cal-renewal)' }}
          onClick={onUndo}
          data-testid="cal-menu-undo"
        >
          <Icon name="undo" size={14} aria-hidden />
          되돌리기
        </button>
      )}
      <button
        type="button"
        role="menuitem"
        className="kuun-menu-item"
        style={menuItem}
        onClick={onEdit}
        data-testid="cal-menu-edit"
      >
        <Icon name="pen" size={14} aria-hidden />
        수정
      </button>
      <button
        type="button"
        role="menuitem"
        className="kuun-menu-item"
        style={{ ...menuItem, color: 'var(--fg-danger)' }}
        onClick={onDelete}
        data-testid="cal-menu-delete"
      >
        <Icon name="trash" size={14} aria-hidden />
        삭제
      </button>
    </div>
  );
}

// ── 수정 ─────────────────────────────────────────────────────────────────

export function EditScheduleModal({
  target,
  onClose,
}: {
  target: ScheduleTarget & { move: ScheduleMove };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const [date, setDate] = useState(target.date);
  const [time, setTime] = useState(target.time ?? '');
  const [memo, setMemo] = useState(target.memo ?? '');
  const [error, setError] = useState<string | null>(null);
  const label = CATEGORY_META[target.category].label;
  const memoEditable =
    (target.move.kind === 'maintenance' && !!target.fromCalendar) ||
    target.move.kind === 'replace' ||
    target.move.kind === 'renewal';

  const save = useMutation({
    mutationFn: async () => {
      const m = target.move;
      const t = time || null;
      if (m.kind === 'infra') await scheduleDatesApi.moveInfra(m.id, m.field, date, t);
      else if (m.kind === 'return') await scheduleDatesApi.moveReturn(m.id, date, t);
      else if (m.kind === 'replace')
        await scheduleDatesApi.updateReplacement(m.id, { date, time: t, memo });
      else if (m.kind === 'renewal')
        await scheduleDatesApi.updateRenewal(m.id, { date, time: t, memo });
      else if (m.kind === 'event')
        await scheduleDatesApi.updateEvent(
          m.id,
          m.side === 'install'
            ? { installDate: date, installTime: t }
            : { returnDate: date, returnTime: t },
        );
      else await scheduleDatesApi.moveMaintenance(m.id, date, t, memoEditable ? memo : undefined);
    },
    onSuccess: async () => {
      await refreshAfterChange(qc);
      toast?.success(
        `${label} 일정을 고쳤습니다. 새 일정은 ${date} ${time || '하루 종일'}입니다. 1분 안에 구글 캘린더에도 반영됩니다.`,
      );
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`${label} 일정 수정`}
      width={460}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!date || save.isPending}
            data-testid="edit-schedule-save"
          >
            {save.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={targetRow}>
          <Icon name={CATEGORY_META[target.category].iconName} size={14} aria-hidden />
          <b>{target.title}</b>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ ...field, flex: '1 1 150px' }}>
            <span style={labelSt}>날짜</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={input}
              data-testid="edit-schedule-date"
            />
          </label>
          <label style={{ ...field, flex: '2 1 260px' }}>
            <span style={labelSt}>
              시간 <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>(선택)</span>
            </span>
            {/* 2026-10-22 오너 — [지우기] 버튼 · 아래 안내 문구를 뺐다. 비우려면 첫 칸을 '선택'으로. */}
            <TimeSelect
              value={time}
              onChange={setTime}
              style={input}
              ariaLabel="시간 (선택)"
              testId="edit-schedule-time"
            />
          </label>
        </div>
        {target.replacement?.done && (
          <span style={warnSt} data-testid="edit-schedule-replace-done">
            교체를 마친 일정입니다. 날짜나 시간을 바꾸면 실제 교체일도 같이 바뀝니다. 뺀 로봇의
            회수일과 넣은 로봇의 배치일이 함께 바뀝니다.
          </span>
        )}
        {target.renewal?.done && (
          <span style={warnSt} data-testid="edit-schedule-renewal-done">
            마친 재계약입니다. 일정 날짜만 바뀌고 실제 회수일과 배치일은 그대로입니다.
          </span>
        )}
        {memoEditable && (
          <label style={field}>
            <span style={labelSt}>작업 메모</span>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={3}
              maxLength={2000}
              style={{ ...input, height: 'auto', resize: 'vertical' }}
              data-testid="edit-schedule-memo"
            />
            <span style={hint}>
              {target.move.kind === 'replace' || target.move.kind === 'renewal'
                ? '구글 캘린더 설명에도 반영됩니다.'
                : target.reportNo
                  ? '구글 캘린더 설명에 반영되고, 아직 고치지 않았다면 고객사 리포트의 요약에도 반영됩니다.'
                  : '구글 캘린더 설명에 반영되고, 현장에서 아직 고치지 않았다면 고장 조치 이력의 증상 요약에도 반영됩니다.'}
            </span>
          </label>
        )}
        {error && (
          <div role="alert" style={errorSt} data-testid="edit-schedule-error">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── 삭제 ─────────────────────────────────────────────────────────────────

export function deleteMessage(target: ScheduleTarget): string {
  const m = target.move;
  const head = `${target.date} '${target.title}' 일정을 삭제합니다.`;
  const tail = ' 구글 캘린더에서도 1분 안에 지워집니다.';
  if (!m) return head + tail;
  if (m.kind === 'maintenance') {
    return target.fromCalendar
      ? target.reportNo
        ? `${head} 이 일정으로 만든 서비스 오더와 고객사 리포트(${target.reportNo})도 함께 삭제됩니다.${tail}`
        : `${head} 이 일정으로 만든 서비스 오더와 '리포트 · 조치 이력'의 고장 조치 이력도 함께 삭제됩니다.${tail}`
      : `${head} 서비스 오더는 남고 방문 예정일만 비워집니다.${tail}`;
  }
  // 2026-10-22 오너 — 일정을 지우면 '회수 예정' 상태도 풀린다(서버 moveReturnDate).
  if (m.kind === 'return')
    return `${head} 고객사의 회수 예정일이 비워지고, '회수 예정' 상태도 풀려 현장 앱 '로봇 회수' 탭에서 빠집니다.${tail}`;
  // 2026-10-22 오너 — "완료한 재계약을 캘린더에서 삭제하면 일정만 삭제되게".
  if (m.kind === 'renewal')
    return target.renewal?.done
      ? `${head} 마친 재계약이라 일정만 지웁니다. 로봇과 계약은 그대로입니다. 재계약을 되돌리려면 [되돌리기]를 눌러 주세요.${tail}`
      : `${head} 로봇과 계약은 바뀌지 않습니다.${tail}`;
  if (m.kind === 'event') {
    const n = target.calendarEvent?.returned ? 0 : (target.calendarEvent?.robots.length ?? 0);
    return `${head} 이벤트 설치와 이벤트 회수 일정이 함께 지워집니다.${
      n > 0 ? ` 배정해 둔 로봇 ${n}대는 배정이 풀려 다시 재고로 돌아갑니다.` : ''
    }${tail}`;
  }
  if (m.kind === 'replace')
    return hasDoneSwap(target)
      ? [
          `${head} 이미 한 교체를 되돌립니다.`,
          '· 넣은 로봇은 운영 중에서 빠져 배치하기 전 상태로 돌아갑니다.',
          '· 뺐던 로봇이 다시 그 매장에서 운영 중으로 돌아오고, 계약도 그 로봇으로 돌아갑니다.',
          `· 로봇 교체 서비스 오더는 취소됩니다.${tail}`,
        ].join('\n\n')
      : `${head} 로봇 교체 서비스 오더가 취소됩니다. 로봇 배치는 그대로입니다.${tail}`;
  return `${head} 인프라 발주는 남고 이 날짜만 비워집니다.${tail}`;
}

export function DeleteScheduleDialog({
  target,
  onClose,
}: {
  target: ScheduleTarget & { move: ScheduleMove };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const [busy, setBusy] = useState(false);
  const label = CATEGORY_META[target.category].label;

  async function confirm() {
    setBusy(true);
    try {
      const m = target.move;
      if (m.kind === 'infra') await scheduleDatesApi.clearInfra(m.id, m.field);
      else if (m.kind === 'return') await scheduleDatesApi.clearReturn(m.id);
      else if (m.kind === 'replace') await scheduleDatesApi.cancelReplacement(m.id);
      else if (m.kind === 'renewal') await scheduleDatesApi.deleteRenewal(m.id);
      else if (m.kind === 'event') await scheduleDatesApi.deleteEvent(m.id);
      else await scheduleDatesApi.deleteMaintenance(m.id);
    } catch (err) {
      setBusy(false);
      throw new Error(extractApiError(err));
    }
    await refreshAfterChange(qc);
    toast?.success(
      hasDoneSwap(target)
        ? '로봇 교체를 되돌리고 일정을 삭제했습니다.'
        : `${label} 일정을 삭제했습니다.`,
    );
    setBusy(false);
    onClose();
  }

  return (
    <ConfirmDialog
      open
      title={hasDoneSwap(target) ? '로봇 교체 되돌리기' : `${label} 일정 삭제`}
      message={deleteMessage(target)}
      confirmLabel={hasDoneSwap(target) ? '되돌리고 삭제' : '삭제'}
      danger
      busy={busy}
      onConfirm={confirm}
      onCancel={onClose}
    />
  );
}

// ── 모양 ─────────────────────────────────────────────────────────────────

const menu: CSSProperties = {
  position: 'fixed',
  zIndex: 1000,
  minWidth: 132,
  padding: 4,
  borderRadius: 8,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.14)',
  display: 'flex',
  flexDirection: 'column',
};
const menuItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  border: 'none',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--fg-default)',
  fontSize: 13,
  fontWeight: 600,
  textAlign: 'left',
  cursor: 'pointer',
};
const targetRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-sunken)',
  fontSize: 13,
};
const field: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelSt: CSSProperties = { fontSize: 13, fontWeight: 600 };
const input: CSSProperties = {
  width: '100%',
  height: 36,
  padding: '6px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  boxSizing: 'border-box',
};
const hint: CSSProperties = { fontSize: 12, color: 'var(--fg-muted)' };
const warnSt: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-default)',
  fontSize: 12,
};
const errorSt: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  fontSize: 13,
};
